<template>
    <div class="w-100">
        <img src="/img/logo_icon.svg" style="height: 35px;" class="mb-4" />

        <loading v-if="loading"></loading>
        <div v-else>
            <!-- Quick access alert -->
            <div v-if="savedPortal">
                <h1 class="mt-0 mb-3">Welcome back 👋</h1>
                <p>You were last logged into the portal: <strong>{{ savedPortal.name }}</strong></p>
                <hr>
                <div class="d-flex justify-content-between">
                    <b-button variant="primary" @click="quickAccess">
                        Go to {{ savedPortal.name }}
                    </b-button>
                    <b-button variant="outline" @click="clearSavedPortal">
                        Open a different portal
                    </b-button>
                </div>
            </div>

            <div v-else>
                <!-- Step 1: Ask email and password -->
                <div v-if="step === 1">
                    <h1 class="mt-0 mb-3">Log in to ProductLift 👋</h1>
                    <p class="mb-5">Enter your credentials to access your product feedback and roadmap hub.</p>

                    <validation-observer ref="observer" v-slot="{ passes }" :lazy="true">
                        <form @submit.stop.prevent="passes(findPortals)">
                            <p class="mt-3 mb-1">Email</p>
                            <validation-provider name="email" rules="email|required" v-slot="{ errors }">
                                <b-form-input type="email" autocomplete="email" v-model="email" placeholder="Enter your email"></b-form-input>
                                <span class="validation">{{ errors[0] }}</span>
                            </validation-provider>

                            <p class="mt-3 mb-1">Password</p>
                            <validation-provider name="password" rules="required" v-slot="{ errors }">
                                <b-form-input type="password" autocomplete="current-password" v-model="password" placeholder="••••••••••"></b-form-input>
                                <span class="validation">{{ errors[0] }}</span>
                            </validation-provider>

                            <button type="submit" class="btn btn-primary btn-block mt-3" id="find_portals">Log in</button>
                            <p class="small mt-5">Don't have an account? <a href="/signup">Create an account</a></p>
                        </form>
                    </validation-observer>
                </div>

                <!-- Step 2: Choose portal if multiple -->
                <div v-if="step === 2">
                    <h2 class="mb-3">Select a portal to log in</h2>
                    <b-list-group>
                        <b-list-group-item
                            v-for="portal in portals"
                            :key="portal.id"
                            button
                            @click="loginToPortal(portal)"
                        >
                            {{ portal.name }}<br>
                            <span class="small text-muted">{{ portal.domain }}</span>
                        </b-list-group-item>
                    </b-list-group>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NotificationPop from '../mixins/NotificationPop.js'
import Loading from "./Loading";
import { mapState } from "vuex";

export default {
    name: "LoginPortalSelector",
    mixins: [NotificationPop],
    components: {
        Loading
    },

    data() {
        return {
            loading: false,
            step: 1,
            email: null,
            password: null,
            portals: [],
            hash_login_url: null,
            portal_url: null,
            savedPortal: null,
        }
    },

    computed: {
        ...mapState(['environment']),
    },

    mounted() {
        this.checkSavedPortal();
    },

    methods: {
        checkSavedPortal() {
            this.$http.get('/api/check-saved-portal')
                .then(({ data }) => {
                    if (data.portal) {
                        this.savedPortal = data.portal;
                    }
                })
                .catch(error => {
                    console.error('Error checking saved portal:', error);
                });
        },

        quickAccess() {
            this.loading = true;
            this.$http.post('/api/quick-access', { portalId: this.savedPortal.id })
                .then(({ data }) => {
                    window.location.href = data.redirect_url;
                })
                .catch(error => {
                    this.loading = false;
                    this.popNotification('Could not access the portal. Please log in manually.', 'danger');
                });
        },

        clearSavedPortal() {
            this.savedPortal = null;

            this.$http.post('/api/clear-saved-portal')
                .then(() => {
                })
                .catch(error => {
                    console.error('Error clearing saved portal:', error);
                });
        },

        findPortals() {
            this.loading = true;
            this.$http.post('/api/find-portals', {
                email: this.email,
                password: this.password,
            })
                .then(({ data }) => {
                    this.loading = false;
                    this.portals = data.portals;

                    if (this.portals.length === 1) {
                        this.loginToPortal(this.portals[0]);
                    } else if (this.portals.length > 1) {
                        this.step = 2;
                    } else {
                        this.popNotification('No portals found for this email and password combination.', 'warning');
                    }
                })
                .catch(error => {
                    this.loading = false;
                    this.popNotification('Could not find portals. Please try again.', 'danger');
                });
        },

        loginToPortal(portal) {
            this.loading = true;
            this.$http.post('/api/generate-login-hash', {
                portalId: portal.id,
                email: this.email,
                password: this.password,
            })
                .then(({ data }) => {
                    this.hash_login_url = data.hash;

                    // Open the URL in a new browser tab
                    window.location.href = this.hash_login_url;

                    this.$confetti.start({
                        particles: [
                            {
                                type: 'heart',
                                size: 14,
                            }
                        ],
                    });
                    setTimeout(() => this.$confetti.stop(), 2000);
                })
                .catch(error => {
                    this.loading = false;
                    this.popNotification('Could not log in. Please try again.', 'danger');
                });
        },
    }
}
</script>